import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Truck, UserPlus, CheckCircle2 } from "lucide-react";

export function AssignAgentDialog({
  open,
  onOpenChange,
  assignTarget,
  assignB2BMode,
  assignB2BRole,
  assignCrewMode,
  crewNeeded,
  selectedCrew,
  selectedAgent,
  setSelectedAgent,
  vehicleType,
  setVehicleType,
  agentsList,
  onAssign,
  onToggleCrewMember,
}) {
  let assignTitle = "Assign Agent";
  let assignDesc = "Select a delivery agent for this order";
  if (assignB2BMode) {
    assignTitle = `B2B ${assignB2BRole === "delivery" ? "Delivery" : "Pickup"} Agent`;
    assignDesc = `Assign ${assignB2BRole} agent with vehicle for B2B shipment`;
  } else if (assignCrewMode) {
    assignTitle = "Assign Crew";
    assignDesc = `Select ${crewNeeded} crew members for this order`;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="assign-agent-dialog" aria-describedby="assign-agent-desc">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-semibold text-slate-900">{assignTitle}</DialogTitle>
          <p id="assign-agent-desc" className="text-sm text-slate-500">{assignDesc}</p>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <p className="text-sm text-slate-500">Order: <span className="font-mono font-semibold text-slate-900">{assignTarget}</span></p>

          {assignB2BMode ? (
            <>
              {/* Vehicle Type Picker */}
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-2">Vehicle Type</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "bike", label: "Bike", icon: "🏍️", desc: "Small packages" },
                    { value: "van", label: "Van", icon: "🚐", desc: "Medium loads" },
                    { value: "truck", label: "Truck", icon: "🚛", desc: "Bulk cargo" },
                  ].map((v) => (
                    <button
                      key={v.value}
                      onClick={() => setVehicleType(v.value)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        vehicleType === v.value
                          ? "border-[#FF6B00] bg-orange-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`vehicle-${v.value}`}
                    >
                      <span className="text-2xl block">{v.icon}</span>
                      <span className={`text-xs font-bold block mt-1 ${vehicleType === v.value ? "text-[#FF6B00]" : "text-slate-700"}`}>{v.label}</span>
                      <span className="text-[9px] text-slate-400">{v.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* Agent Selection */}
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger className="rounded-xl border-slate-200 h-11" data-testid="b2b-assign-agent-select">
                  <SelectValue placeholder={`Select ${assignB2BRole} agent`} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {agentsList.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.email}) {a.is_online ? "- Online" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={onAssign} disabled={!selectedAgent} className="w-full bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-12 font-semibold btn-press" data-testid="b2b-assign-confirm-btn">
                <Truck className="h-5 w-5 mr-2" />
                Assign {assignB2BRole} Agent with {vehicleType}
              </Button>
            </>
          ) : assignCrewMode ? (
            <>
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-3 py-2">
                <span className="text-xs font-semibold text-blue-700">AI Detected Crew Size</span>
                <span className="text-sm font-bold text-blue-900">{crewNeeded} agents needed</span>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="crew-agent-list">
                {agentsList.map((a) => {
                  const isSelected = selectedCrew.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => onToggleCrewMember(a.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                        isSelected ? "border-[#FF6B00] bg-orange-50/50" : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`crew-agent-${a.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                        isSelected ? "bg-[#FF6B00] text-white" : "bg-slate-100 text-slate-500"
                      }`}>
                        {isSelected ? <CheckCircle2 className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${isSelected ? "text-[#FF6B00]" : "text-slate-700"}`}>{a.name}</p>
                        <p className="text-[10px] text-slate-400">{a.email} {a.is_online ? "- Online" : ""}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 text-center">
                Selected: <span className={`font-bold ${selectedCrew.length >= crewNeeded ? "text-green-600" : "text-[#FF6B00]"}`}>{selectedCrew.length}</span> / {crewNeeded}
              </p>
              <Button onClick={onAssign} disabled={selectedCrew.length === 0} className="w-full bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-12 font-semibold btn-press" data-testid="assign-crew-confirm-btn">
                <UserPlus className="h-5 w-5 mr-2" />
                Assign {selectedCrew.length} Crew Member{selectedCrew.length !== 1 ? "s" : ""}
              </Button>
            </>
          ) : (
            <>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger className="rounded-xl border-slate-200 h-11" data-testid="assign-agent-select">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {agentsList.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.email}) {a.is_online ? "- Online" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={onAssign} disabled={!selectedAgent} className="w-full bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-12 font-semibold btn-press" data-testid="assign-agent-confirm-btn">
                <UserPlus className="h-5 w-5 mr-2" />
                Assign Agent
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AssignAgentDialog;
